import { List, Map } from 'immutable';
import { isEqual } from 'lodash';
import { getTextStylesFromBrandStyle } from 'modules/BrandDefinition';
import { isValidHeadingTag } from 'modules/Lexical/Plugins/HeadingTagPlugin/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IMap } from 'typings/DeepIMap';
import * as Constants from 'const';
import * as Models from 'models';
import { getChangedStylesToBeKept } from 'utils/styles/getStylesToBeKept';
import { getPrioritizedRelation } from '../utils/relation';
import { Styles, StylesSetters, getInitialStyles, stylesFromSource } from '../utils/styles';

const emptyRelationStyles = Map();

type HookProps = {
  relation: Models.LayeredRegularRelationMap<Models.TextRelationStyles>;
  activeLayer: Constants.Layer;
  isAutoFitContent: boolean;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyles: Models.BrandStylesMap;
  brandStyleChangedFromEditor: boolean;
  onBrandStyleChangedFromEditor: (value: boolean) => void;
};

function getSource(props: Pick<HookProps, 'relation' | 'activeLayer'>): IMap<Models.TextRelationStyles> {
  const { relation, activeLayer } = props;

  return relation.getIn(['styles', activeLayer]) || emptyRelationStyles;
}

function isBrandStyleChanged(
  brandStyleDefaults: Styles,
  currentStyles: Styles,
): boolean {
  const compareProperty = (key: keyof Styles): boolean => {
    const defaultValue = brandStyleDefaults[key];
    const current = currentStyles[key];

    if (defaultValue && typeof defaultValue === 'object' && 'equals' in defaultValue && typeof defaultValue.equals === 'function') {
      return !defaultValue.equals(current);
    }

    return !isEqual(defaultValue, current);
  };

  const propertiesToCompare: (keyof Styles)[] = [
    'backgroundColor',
    'backgroundGradient',
    'backgroundColorOpacity',
    'padding',
    'verticalAlignment',
  ];

  return propertiesToCompare.some(compareProperty);
}

type StylesHook = {
  styles: Styles;
  stylesSetters: StylesSetters;
  ensureStylesToBeKept: () => void;
  applyBrandStyle: (
    brandStyle: Models.BrandStyleMap,
    brandStyleValues: Models.TextBrandStyles,
  ) => void;
  hasBrandStyleHeadingTag: boolean;
};

export default function useStyles(props: HookProps): StylesHook {
  const prevPropsRef = useRef(props);
  const {
    relation,
    activeLayer,
    colors,
    fonts,
    brandStyles,
    brandStyleChangedFromEditor,
    onBrandStyleChangedFromEditor,
  } = props;

  const source = getSource(props);

  const initialStyles = useMemo(
    () => getInitialStyles(source, colors, fonts, brandStyles),
    [source, colors, fonts, brandStyles],
  );

  const [styles, setStyles] = useState<Styles>(() => initialStyles);
  const { textAlign } = getTextStylesFromBrandStyle(styles.brandStyle, List([]), List([]));
  const isListBrandStyle = textAlign === Constants.TextHorizontalAlignmentType.UNORDERED_LIST;
  const hasBrandStyleHeadingTag = isValidHeadingTag(styles.brandStyle?.get('tag')) && !isListBrandStyle;

  const brandStyleDefaults = useMemo(() => {
    if (!styles.brandStyle) {
      return initialStyles;
    }

    const textStyles = getTextStylesFromBrandStyle(styles.brandStyle, colors, fonts);

    return {
      ...initialStyles,
      backgroundColor: textStyles.backgroundColor,
      backgroundGradient: undefined,
      padding: textStyles.padding,
      verticalAlignment: textStyles.verticalAlignment,
    };
  }, [styles.brandStyle, colors, fonts, initialStyles]);

  const setters = useMemo((): StylesSetters => {
    return {
      backgroundColor: color => setStyles(prev => ({
        ...prev,
        backgroundColor: color,
        backgroundGradient: undefined,
        brandStyleChanged: isBrandStyleChanged(brandStyleDefaults,
          {
            ...prev,
            backgroundColor: color,
            backgroundGradient: undefined,
          }) || brandStyleChangedFromEditor,
      })),
      backgroundColorOpacity: backgroundColorOpacity => setStyles(prev => ({
        ...prev,
        backgroundColorOpacity,
        brandStyleChanged: isBrandStyleChanged(brandStyleDefaults, { ...prev, backgroundColorOpacity }) || brandStyleChangedFromEditor,
      })),
      backgroundGradient: (gradient, backupColor) => setStyles(prev => ({
        ...prev,
        backgroundColor: backupColor,
        backgroundGradient: gradient,
        brandStyleChanged: isBrandStyleChanged(brandStyleDefaults, {
          ...prev, backgroundColor: backupColor,
          backgroundGradient: gradient,
        }) || brandStyleChangedFromEditor,
      })),
      backgroundImage: backgroundImage => setStyles(prev => ({ ...prev, backgroundImage })),
      border: border => setStyles(prev => ({ ...prev, border })),
      borderRadius: borderRadius => setStyles(prev => ({ ...prev, borderRadius })),
      brandStyle: (brandStyle, iterator?: number) => setStyles(prev => ({
        ...prev,
        brandStyle,
        brandStyleVersion: iterator ?? prev.brandStyleVersion + 1,
      })),
      brandStyleChanged: (value: boolean): void => {
        setStyles((prev) => {
          const brandStyleChangedResult = isBrandStyleChanged(brandStyleDefaults, prev) || value || brandStyleChangedFromEditor;

          return ({
            ...prev,
            brandStyleChanged: brandStyleChangedResult,
          });
        });
      },
      padding: padding => setStyles(prev => ({
        ...prev,
        padding,
        brandStyleChanged: isBrandStyleChanged(brandStyleDefaults, { ...prev, padding }) || brandStyleChangedFromEditor,
      })),
      verticalAlignment: verticalAlignment => setStyles(prev => ({
        ...prev,
        verticalAlignment,
        brandStyleChanged: isBrandStyleChanged(brandStyleDefaults, { ...prev, verticalAlignment }) || brandStyleChangedFromEditor,
      })),
    };
  }, [brandStyleDefaults, brandStyleChangedFromEditor]);

  // TODO: it looks like overhead
  const ensureStylesToBeKept = useCallback((): void => {
    const newStyles = stylesFromSource(
      relation.getIn(['styles', activeLayer]),
      brandStyles,
      colors,
    );

    const {
      backgroundColor,
      backgroundColorTint,
      backgroundColorOpacity,
      backgroundGradient,
      backgroundImage,
      border,
      padding,
    } = getChangedStylesToBeKept(
      getPrioritizedRelation({ relation, activeLayer }),
      brandStyles,
      colors,
      fonts,
    );

    if (backgroundGradient) {
      setters.backgroundGradient(newStyles?.backgroundGradient, newStyles?.backgroundColor);
    } else if (backgroundColor || backgroundColorTint) {
      setters.backgroundColor(newStyles?.backgroundColor);
    }

    if (backgroundColorOpacity) {
      setters.backgroundColorOpacity(backgroundColorOpacity);
    }

    if (backgroundImage) {
      setters.backgroundImage(newStyles?.backgroundImage);
    }

    if (border) {
      setters.border(newStyles?.border);
    }

    if (padding) {
      setters.padding(newStyles?.padding);
    }
  }, [relation, activeLayer, fonts, colors, brandStyles, setters]);

  const applyBrandStyle = useCallback((
    brandStyle: Models.BrandStyleMap,
    brandStyleValues: Models.TextBrandStyles,
  ) => {
    onBrandStyleChangedFromEditor(false);
    setStyles(prev => ({
      ...prev,
      padding: brandStyleValues.padding,
      verticalAlignment: brandStyleValues.verticalAlignment,
      backgroundColor: brandStyleValues.backgroundColor,
      backgroundGradient: undefined,
      brandStyle,
      brandStyleVersion: prev.brandStyleVersion + 1,
      brandStyleChanged: false,
    }),
    );
  }, [setStyles, onBrandStyleChangedFromEditor]);

  useEffect(() => {
    if (source.get('brandStyleChanged') && !brandStyleChangedFromEditor) {
      onBrandStyleChangedFromEditor(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // skip run on mount
    if (prevPropsRef.current === props) {
      return;
    }

    const prevBrandStyleChangedFromEditor = prevPropsRef.current.brandStyleChangedFromEditor;
    const brandStyleChangedFromEditorUpdated = prevBrandStyleChangedFromEditor !== brandStyleChangedFromEditor;

    // we check that the styles is changed without autofit content as in some cases it causes unnecessary triggers of state sync
    const preparedCurrentStyles = source.has('isAutoFitContent')
      ? source.delete('isAutoFitContent')
      : source;
    const prevSource = getSource(prevPropsRef.current);
    const preparedPrevStyles = prevSource.has('isAutoFitContent')
      ? prevSource.delete('isAutoFitContent')
      : prevSource;

    const sourceChanged = !preparedCurrentStyles.equals(preparedPrevStyles);

    if (!sourceChanged && !brandStyleChangedFromEditorUpdated) {
      return;
    }

    setStyles((prev) => {
      if (!sourceChanged) {
        const recalculated = isBrandStyleChanged(brandStyleDefaults, prev) || brandStyleChangedFromEditor;

        return { ...prev, brandStyleChanged: recalculated };
      }

      const brandStyleChanged = source.get('brandStyleChanged') || isBrandStyleChanged(brandStyleDefaults, prev) || brandStyleChangedFromEditor;

      return {
        ...stylesFromSource(source, brandStyles, colors),
        brandStyle: brandStyles.get(source.get('brandStyleId') || Constants.Styles.DefaultTextBrandStyle),
        brandStyleVersion: prev.brandStyleVersion + 1,
        brandStyleChanged,
      };
    });
  }, [source, relation.get('id'), activeLayer, brandStyleChangedFromEditor, brandStyleDefaults]);

  useEffect(() => {
    prevPropsRef.current = props;
  });

  return {
    styles,
    stylesSetters: setters,
    ensureStylesToBeKept,
    applyBrandStyle,
    hasBrandStyleHeadingTag,
  };
}
